import React, { useMemo, useState, useEffect } from 'react';
import { compile } from 'mathjs';
import Plot from 'react-plotly.js';
import './GraphPlotter.css';

function GraphPlotter({ functions, xMin, xMax, yMin, yMax, gridSize }) {
  const [isMobileLegend, setIsMobileLegend] = useState(false);

  useEffect(() => {
    const updateMobileLegend = () => setIsMobileLegend(window.innerWidth <= 768);
    updateMobileLegend();
    window.addEventListener('resize', updateMobileLegend);
    return () => window.removeEventListener('resize', updateMobileLegend);
  }, []);

  const data = useMemo(() => {
    const plotData = [];
    
    functions.forEach((func) => {
      try {
        // Compile the expression for better performance
        const compiled = compile(func.expression);

        // Generate x values
        const xValues = [];
        const yValues = [];
        const step = (xMax - xMin) / gridSize;

        for (let x = xMin; x <= xMax; x += step) {
          try {
            // Evaluate the function for this x value
            const y = compiled.evaluate({ x });

            // Check if y is a valid number and within reasonable range
            if (typeof y === 'number' && isFinite(y) && Math.abs(y) < 10000) {
              xValues.push(x);
              yValues.push(y);
            }
          } catch (e) {
            // Skip points where function is undefined
            continue;
          }
        }

        // Add original function
        plotData.push({
          x: xValues,
          y: yValues,
          type: 'scatter',
          mode: 'lines',
          name: func.label || func.expression,
          line: {
            color: func.color,
            width: 2,
          },
          hovertemplate: '<b>%{fullData.name}</b><br>x: %{x:.3f}<br>y: %{y:.3f}<extra></extra>',
        });


      } catch (error) {
        console.error(`Error parsing function "${func.expression}":`, error);
        plotData.push({
          x: [],
          y: [],
          type: 'scatter',
          name: `${func.label || func.expression} (Error)`,
          line: { color: '#e74c3c', width: 2, dash: 'dash' },
        });
      }
    });
    
    return plotData;
  }, [functions, xMin, xMax, gridSize]);

  const layout = {
    title: {
      text: 'Function Grapher',
      font: { size: 18, color: '#333' },
    },
    xaxis: {
      title: 'x',
      range: [xMin, xMax],
      gridcolor: '#e0e0e0',
      showgrid: true,
      zeroline: true,
      zerolinewidth: 2,
      zerolinecolor: '#999',
    },
    yaxis: {
      title: 'f(x)',
      range: [yMin, yMax],
      gridcolor: '#e0e0e0',
      showgrid: true,
      zeroline: true,
      zerolinewidth: 2,
      zerolinecolor: '#999',
    },
    plot_bgcolor: '#fafafa',
    paper_bgcolor: 'white',
    hovermode: 'x unified',
    showlegend: true,
    margin: isMobileLegend ? { l: 60, r: 40, t: 60, b: 100 } : { l: 60, r: 40, t: 60, b: 60 },
    legend: {
      orientation: isMobileLegend ? 'h' : 'v',
      x: isMobileLegend ? 0.5 : 1.02,
      y: isMobileLegend ? -0.18 : 1,
      xanchor: isMobileLegend ? 'center' : 'left',
      yanchor: 'top',
      bgcolor: 'rgba(255, 255, 255, 0.9)',
      bordercolor: '#ddd',
      borderwidth: 1,
    },
  };

  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: [
      'zoom2d',
      'pan2d',
      'select2d',
      'lasso2d',
      'zoomInGeo',
      'zoomOutGeo',
      'resetGeo2d',
      'resetScale2d',
      'toggleSpikelines',
      'hoverClosestCartesian',
      'hoverCompareCartesian',
      'autoScale2d',
      'resetCameraDefault3d',
      'resetCameraLastSave3d',
      'zoomIn2d',
      'zoomOut2d',
    ],
  };

  return (
    <div className="graph-plotter">
      {functions.length === 0 ? (
        <div className="empty-graph">
          <div className="empty-graphic">
            <svg viewBox="0 0 100 100" width="80" height="80">
              <rect x="10" y="10" width="80" height="80" fill="none" stroke="#ddd" strokeWidth="2" />
              <line x1="10" y1="50" x2="90" y2="50" stroke="#ddd" strokeWidth="1" />
              <line x1="50" y1="10" x2="50" y2="90" stroke="#ddd" strokeWidth="1" />
            </svg>
          </div>
          <h2>No functions to display</h2>
          <p>Add a function using the form on the left to get started!</p>
        </div>
      ) : (
        <Plot
          data={data}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
        />
      )}
    </div>
  );
}

export default GraphPlotter;
